'use client'

import { submitBrochureAction } from '@/app/actions/submitBrochure'
import { submitFacebookEventAction } from '@/app/actions/submitFacebookEvent'
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded'
import CloseIcon from '@mui/icons-material/Close'
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material'
import { useReCaptcha } from 'next-recaptcha-v3'
import Link from '@/components/shared/HoverPrefetchLink'
import { usePathname, useSearchParams } from 'next/navigation'
import { useCallback, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

const StayInTouch = ({
  children,
  title = '',
  submitButtonText = 'Download Brochure',
  formInfo = {
    program: '',
    emailSubject: '',
    emailContent: '',
    slug: '',
    utmSource: '',
    utmMedium: '',
    brochureLink: '',
  },
  mockSubmit = false,
}) => {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [emailMessage, setEmailMessage] = useState('')
  const [emailStatus, setEmailStatus] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const honeypotRef = useRef(null)
  const { executeRecaptcha } = useReCaptcha()
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
    },
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })

  const onSubmit = useCallback(
    async (data) => {
      // anti-bot honeypot: if this hidden field is filled, silently drop (likely bot)
      if ((honeypotRef.current?.value || '').trim() !== '') {
        return
      }

      const fullName = (data?.fullName || '').trim()
      const [firstName, ...rest] = fullName.split(' ').filter(Boolean)
      const lastName = rest.join(' ')

      if (!fullName || !data?.email || !data?.phone) {
        setEmailMessage(
          '<i class="fal fa-exclamation-circle tw:mr-2"></i> Please enter your name, email, and phone'
        )
        return
      }

      setEmailMessage('<i class="fas fa-spinner fa-spin"></i>')

      // Mock path: simulate success without calling server action
      if (mockSubmit) {
        const safeBrochureLink = formInfo?.brochureLink || ''
        setEmailStatus('sent')
        setModalOpen(true)

        // Submit Facebook event on form success (fire and forget)
        // Requirement:
        // - 'Lead' when downloading brochure on /programs/* pages
        // - 'CompleteRegistration' for all other success pages (e.g., /executive-education/*)
        const isLeadEvent = pathname?.startsWith('/programs/')
        const fbEventType = isLeadEvent ? 'Lead' : 'CompleteRegistration'
        console.log(
          `🔵 Facebook Event: "${fbEventType}" fired for pathname: ${pathname}`
        )
        submitFacebookEventAction({
          pathname,
          userPhone: data?.phone || '',
          userEmail: data?.email || '',
          eventType: fbEventType,
        })
          .then((res) => {
            if (res?.ok) {
              console.log(`✅ Facebook "${fbEventType}" event success:`, res)
            } else {
              console.log(`⚠️ Facebook "${fbEventType}" event failed:`, res)
            }
          })
          .catch((err) => {
            console.log(`❌ Facebook "${fbEventType}" event error:`, err)
          })

        if (safeBrochureLink) {
          setEmailMessage(
            '<h3>Thank you for downloading the brochure!</h3><p class="tw:mt-2">Your download should start shortly. Additionally, we\'ve sent the brochure to your email address.</p>'
          )
          setIsDownloading(true)
          // Try to open brochure automatically (may be blocked by browser)
          setTimeout(() => {
            if (typeof window !== 'undefined') {
              const newWindow = window.open(
                safeBrochureLink,
                '_blank',
                'noopener,noreferrer'
              )
              if (newWindow) newWindow.opener = null
              setIsDownloading(false)
            }
          }, 1500)
        } else {
          setEmailMessage('<h3>Thank you for your interest!</h3>')
        }
        const newParams = new URLSearchParams(searchParams?.toString() || '')
        newParams.set('submit', 'success')
        const newUrl = `${pathname}?${newParams.toString()}`
        if (typeof window !== 'undefined') {
          window.history.replaceState(null, '', newUrl)
        }
        return
      }

      // Minimal LinkedIn front-end conversion (safe optional)
      try {
        // eslint-disable-next-line no-undef
        typeof window !== 'undefined' &&
          window?.lintrk?.('track', { conversion_id: 9251316 })
      } catch (_) {}

      // reCAPTCHA v3 token
      let token = ''
      try {
        if (!mockSubmit && typeof executeRecaptcha === 'function') {
          token = (await executeRecaptcha('sasin_brochure_form')) || ''
        }
      } catch (_) {
        token = ''
      }

      const emailSubject =
        formInfo?.emailSubject || 'Your Sasin Brochure Download'
      const baseEmailContent = formInfo?.emailContent || ''
      const safeBrochureLink = formInfo?.brochureLink || ''
      const program = formInfo?.program || ''
      const slug = formInfo?.slug || pathname || ''
      const utm_source = formInfo?.utmSource || 'Main web page'
      const utm_medium = formInfo?.utmMedium || 'Email Subscription (Main Web)'

      const email_content =
        baseEmailContent ||
        `
    <div style="padding-top:12px;padding-bottom:12px;width:100%;background-color:#05aeef;height:95px;text-align:center;">
      <img style="margin:8px;" src="https://cms-dev.sasin.edu/wp-content/uploads/2019/11/whitelogo.png" />
    </div>
    <div style="padding:24px;">
      Dear ${fullName},

      Thank you for your interest in Sasin.
      ${
        safeBrochureLink
          ? `You can find the requested brochure here: <a href="${safeBrochureLink}">Download brochure</a>`
          : ''
      }

      If you find that our program aligns with your professional aspirations and learning goals, we encourage you to take the next step by applying for the program. To begin your application journey, please click on the link below:
      <a href="https://app.sasin.edu/" target="_blank">Apply now</a>

      In the meantime, if you have any questions about the program or the application process, feel free to reach out to us at <a href="mailto:admissions@sasin.edu" target="_blank">admissions@sasin.edu</a>.

      We look forward to the possibility of welcoming you to our program.

      Sasin School of Management
    </div>
    <div style="width:100%;background-color:#05aeef;height:30px;"></div>
      `.trim()

      const payload = {
        token,
        firstName: firstName || fullName,
        lastName,
        email: data?.email || '',
        phone: data?.phone || '',
        program,
        email_subject: emailSubject,
        email_content,
        slug,
        utm_source,
        utm_medium,
      }

      try {
        const res = await submitBrochureAction(payload)
        if (res?.ok) {
          setEmailStatus('sent')
          setModalOpen(true)

          // Submit Facebook event on form success (fire and forget)
          // Requirement:
          // - 'Lead' when downloading brochure on /programs/* pages
          // - 'CompleteRegistration' for all other success pages (e.g., /executive-education/*)
          const isLeadEvent = pathname?.startsWith('/programs/')
          const fbEventType = isLeadEvent ? 'Lead' : 'CompleteRegistration'
          console.log(
            `🔵 Facebook Event: "${fbEventType}" fired for pathname: ${pathname}`
          )
          submitFacebookEventAction({
            pathname,
            userPhone: data?.phone || '',
            userEmail: data?.email || '',
            eventType: fbEventType,
          })
            .then((res) => {
              if (res?.ok) {
                console.log(`✅ Facebook "${fbEventType}" event success:`, res)
              } else {
                console.log(`⚠️ Facebook "${fbEventType}" event failed:`, res)
              }
            })
            .catch((err) => {
              console.log(`❌ Facebook "${fbEventType}" event error:`, err)
            })

          if (safeBrochureLink) {
            setEmailMessage(
              '<h3>Thank you for downloading the brochure!</h3><p class="tw:mt-2">Your download should start shortly. Additionally, we\'ve sent the brochure to your email address.</p>'
            )
            setIsDownloading(true)
            // Try to open brochure automatically (may be blocked by browser)
            setTimeout(() => {
              if (typeof window !== 'undefined') {
                const newWindow = window.open(
                  safeBrochureLink,
                  '_blank',
                  'noopener,noreferrer'
                )
                if (newWindow) newWindow.opener = null
                setIsDownloading(false)
              }
            }, 2000)
          } else {
            setEmailMessage('<h3>Thank you for your interest!</h3>')
          }

          const newParams = new URLSearchParams(searchParams?.toString() || '')
          newParams.set('submit', 'success')
          const newUrl = `${pathname}?${newParams.toString()}`
          if (typeof window !== 'undefined') {
            window.history.replaceState(null, '', newUrl)
          }
        } else {
          setEmailMessage('Cannot submit your information')
          setModalOpen(true)
        }
      } catch (error) {
        setEmailMessage('Cannot submit your information')
        setModalOpen(true)
      }
    },
    [formInfo, pathname, searchParams]
  )
  const onFormSubmit = useCallback(
    (e) => {
      e?.preventDefault?.()
      e?.stopPropagation?.()
      handleSubmit(onSubmit)()
    },
    [handleSubmit, onSubmit]
  )

  const handleCloseModal = useCallback(() => {
    setModalOpen(false)
  }, [])

  const handleManualDownload = useCallback(() => {
    const safeBrochureLink = formInfo?.brochureLink || ''
    if (safeBrochureLink && typeof window !== 'undefined') {
      const newWindow = window.open(
        safeBrochureLink,
        '_blank',
        'noopener,noreferrer'
      )
      if (newWindow) newWindow.opener = null
    }
  }, [formInfo?.brochureLink])
  return (
    <div
      className={`tw:bg-primary tw:px-7 tw:max-sm:space-y-8 tw:py-6 tw:rounded tw:relative ${
        children ? 'tw:sm:mr-16' : ''
      }`}
    >
      {/*  */}
      <div className={`${children ? 'tw:sm:w-[60%]' : ''} tw:sm:max-w-[420px]`}>
        <h3
          className="tw:text-2xl tw:text-white tw:font-semibold"
          dangerouslySetInnerHTML={{ __html: title }}
        />
        {/*  */}
        {emailStatus === 'sent' ? (
          <div
            className="tw:my-4 tw:text-white"
            dangerouslySetInnerHTML={{ __html: emailMessage }}
          />
        ) : (
          <form
            className="tw:my-4 tw:space-y-2"
            noValidate
            onSubmit={onFormSubmit}
          >
            {/* anti-bot honeypot — must stay empty; hidden from real users */}
            <input
              ref={honeypotRef}
              type="text"
              name="contact_hp"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              defaultValue=""
              style={{ position: 'absolute', left: '-5000px', width: '1px', height: '1px', opacity: 0 }}
            />

            {/* Full Name */}
            <div className="tw:flex tw:items-end tw:gap-2">
              <i className="far fa-user tw:text-white tw:mb-2" />
              <Controller
                name="fullName"
                control={control}
                rules={{ required: 'Full name is required' }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Full Name"
                    variant="standard"
                    autoComplete="name"
                    error={!!errors.fullName}
                    helperText={errors.fullName?.message || ''}
                    slotProps={{
                      inputLabel: {
                        style: { color: '#fff' },
                      },
                      input: {
                        style: {
                          color: '#fff',
                          fontWeight: 600,
                          borderBottom: '0.5px solid rgba(255, 255, 255, 0.5)',
                        },
                        disableUnderline: true,
                        endAdornment:
                          !fieldState.invalid && field.value ? (
                            <InputAdornment position="end">
                              <CheckCircleRounded
                                sx={{ color: 'white' }}
                                fontSize="small"
                              />
                            </InputAdornment>
                          ) : null,
                      },
                    }}
                  />
                )}
              />
            </div>
            {/* Email */}
            <div className="tw:flex tw:items-end tw:gap-2">
              <i className="far fa-at tw:text-white tw:mb-2" />
              <Controller
                name="email"
                control={control}
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value:
                      /^(?:[a-zA-Z0-9_'^&/+-])+(?:\.(?:[a-zA-Z0-9_'^&/+-])+)*@(?:(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})$/,
                    message: 'Enter a valid email address',
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="email"
                    fullWidth
                    label="Email"
                    variant="standard"
                    autoComplete="email"
                    error={!!errors.email}
                    helperText={errors.email?.message || ''}
                    slotProps={{
                      inputLabel: {
                        style: { color: '#fff' },
                      },
                      input: {
                        style: {
                          color: '#fff',
                          fontWeight: 600,
                          borderBottom: '0.5px solid rgba(255, 255, 255, 0.5)',
                        },
                        disableUnderline: true,
                        endAdornment:
                          !fieldState.invalid && field.value ? (
                            <InputAdornment position="end">
                              <CheckCircleRounded
                                sx={{ color: 'white' }}
                                fontSize="small"
                              />
                            </InputAdornment>
                          ) : null,
                      },
                    }}
                  />
                )}
              />
            </div>
            {/* Phone */}
            <div className="tw:flex tw:items-end tw:gap-2">
              <i className="far fa-phone tw:text-white tw:mb-2" />
              <Controller
                name="phone"
                control={control}
                rules={{ required: 'Phone is required' }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="tel"
                    fullWidth
                    label="Phone"
                    variant="standard"
                    autoComplete="tel"
                    error={!!errors.phone}
                    helperText={errors.phone?.message || ''}
                    slotProps={{
                      inputLabel: {
                        style: { color: '#fff' },
                      },
                      input: {
                        style: {
                          color: '#fff',
                          fontWeight: 600,
                          borderBottom: '0.5px solid rgba(255, 255, 255, 0.5)',
                        },
                        disableUnderline: true,
                        endAdornment:
                          !fieldState.invalid && field.value ? (
                            <InputAdornment position="end">
                              <CheckCircleRounded
                                sx={{ color: 'white' }}
                                fontSize="small"
                              />
                            </InputAdornment>
                          ) : null,
                      },
                    }}
                  />
                )}
              />
            </div>
            <div className="tw:my-4">
              {/* Submit */}
              <Button
                startIcon={
                  isSubmitting ? (
                    <i className="fas fa-spinner fa-spin tw:text-white" />
                  ) : (
                    <i
                      className={`fal tw:text-white ${
                        formInfo?.brochureLink
                          ? 'fa-cloud-download'
                          : 'fa-paper-plane tw:text-sm'
                      }`}
                    />
                  )
                }
                variant="text"
                type="submit"
                disabled={isSubmitting}
                sx={{
                  opacity: isSubmitting ? 0.7 : 1,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                }}
              >
                <span
                  className="tw:text-white tw:tracking-wider"
                  dangerouslySetInnerHTML={{
                    __html: isSubmitting
                      ? 'Submitting...'
                      : formInfo?.brochureLink
                      ? submitButtonText
                      : 'Submit',
                  }}
                />
              </Button>
            </div>
            <p className="tw:text-white tw:text-xs tw:opacity-80">
              By submitting an online form on our website, you hereby agree to
              Sasin School of Management's{' '}
              <Link
                className="tw:text-white tw:underline tw:underline-offset-4"
                target="_blank"
                href="/page/privacy-policy"
              >
                privacy policy.
              </Link>
            </p>
            {/*  */}
          </form>
        )}
        {/*  */}
      </div>
      {/* Follow Sasin */}
      {children ? (
        <div className="tw:sm:absolute tw:bottom-8 tw:sm:left-[62.5%] tw:bg-white tw:p-8 tw:sm:p-12 tw:rounded tw:min-w-[60%] tw:shadow-2xl">
          <div>{children}</div>
        </div>
      ) : null}

      {/* Submission Status Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              overflow: 'visible',
            },
          },
        }}
      >
        <IconButton
          aria-label="close"
          onClick={handleCloseModal}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent
          sx={{
            textAlign: 'center',
            py: 5,
            px: 4,
          }}
        >
          {emailStatus === 'sent' ? (
            <div className="tw:flex tw:flex-col tw:items-center tw:gap-4">
              <div className="tw:w-16 tw:h-16 tw:rounded-full tw:bg-green-100 tw:flex tw:items-center tw:justify-center">
                <CheckCircleRounded sx={{ color: '#22c55e', fontSize: 40 }} />
              </div>
              <div
                className="tw:text-gray-800"
                dangerouslySetInnerHTML={{ __html: emailMessage }}
              />
              {formInfo?.brochureLink ? (
                <div className="tw:mt-4 tw:space-y-3">
                  {isDownloading ? (
                    <p className="tw:text-sm tw:text-gray-500 tw:flex tw:items-center tw:justify-center tw:gap-2">
                      <i className="fas fa-spinner fa-spin" />
                      <span>Attempting to start download...</span>
                    </p>
                  ) : null}
                  <p className="tw:text-sm tw:text-gray-500">
                    If your download didn&apos;t start automatically, click the
                    button below:
                  </p>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleManualDownload}
                    startIcon={<i className="fal fa-cloud-download" />}
                    sx={{
                      textTransform: 'none',
                      px: 4,
                      py: 1.5,
                    }}
                  >
                    Download Brochure
                  </Button>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="tw:flex tw:flex-col tw:items-center tw:gap-4">
              <div className="tw:w-16 tw:h-16 tw:rounded-full tw:bg-red-100 tw:flex tw:items-center tw:justify-center">
                <i className="fas fa-exclamation-circle tw:text-red-500 tw:text-4xl" />
              </div>
              <div className="tw:text-gray-800">
                <h3 className="tw:text-xl tw:font-semibold tw:mb-2">
                  Submission Failed
                </h3>
                <p
                  className="tw:text-gray-600"
                  dangerouslySetInnerHTML={{ __html: emailMessage }}
                />
              </div>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCloseModal}
                sx={{
                  textTransform: 'none',
                  mt: 2,
                }}
              >
                Try Again
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default StayInTouch
